/**
 * Trip planning API client.
 *
 * Uses `getHttpClient()` from `@ufc/core` so auth, base URL and tracing headers
 * are handled the same way as every other domain.
 */

import { getHttpClient } from '@ufc/core'

import type {
  CommitResponse,
  DeliveryRouteDto,
  IntakeResponse,
  LiveTripsResponse,
  FleetMapResponse,
  DriverMilestone,
  PlanRequest,
  PlanResponse,
} from '../../domain/types/api.types'

const ENDPOINTS = {
  plan: '/api/trip-plans/plan',
  commit: '/api/trip-plans/commit',
  readiness: '/api/trip-plans/readiness',
  live: '/api/trip-plans/live',
  milestone: '/api/trip-plans/milestone',
  routes: '/api/delivery-routes',
  intake: '/api/trip-plans/intake',
  arrival: '/api/trip-plans/arrival',
  gps: '/api/trip-plans/gps',
  settings: '/api/planning-parameter',
} as const

/**
 * Both POSTs require an `Idempotency-Key`, per the platform standard. Planning
 * is a pure computation so a repeat is harmless, but committing writes plan
 * rows — a retried commit without a key would create a second version of a plan
 * nobody asked to re-plan.
 */
function idempotencyKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export interface ReadinessResponse {
  generatedAt: string
  etaSource: 'PLAN' | 'GPS'
  etaSourceNote: string
  count: number
  queue: Array<{
    truckPlate: string
    truckType: string
    driverName: string | null
    status: string
    flag: string
    readyAt: number | null
    etaToPlant: number | null
    currentTripReferenceId: string | null
    hasNextAssignment: boolean
    delayMinutes: number | null
    warnings: string[]
  }>
}

export interface PlanningSettings {
  plantOpen: number
  plantClose: number
  loadingMinutes: number
  readyBufferMinutes: number
  serviceMinutes: number
  truckReadyBufferMinutes: number
  gateInMinutes: number
  truckCapacityMt: number
  banPrecedence: 'customer-overrides-route' | 'route-only'
  replanThresholdMinutes: number
  etaNotifyThresholdMinutes: number
  utcOffsetMinutes: number
}

export const tripPlanningApi = {
  /** Compute a plan. Writes nothing. */
  async plan(request: PlanRequest): Promise<PlanResponse> {
    const client = getHttpClient()
    const response = await client.post(ENDPOINTS.plan, request, {
      headers: { 'Idempotency-Key': idempotencyKey() },
    })
    return response.data as PlanResponse
  },

  /** Compute and persist as draft plans. */
  async commit(request: PlanRequest): Promise<CommitResponse> {
    const client = getHttpClient()
    const response = await client.post(ENDPOINTS.commit, request, {
      headers: { 'Idempotency-Key': idempotencyKey() },
    })
    return response.data as CommitResponse
  },

  async readiness(): Promise<ReadinessResponse> {
    const client = getHttpClient()
    const response = await client.get(ENDPOINTS.readiness)
    return response.data as ReadinessResponse
  },

  async intake(planDate: string): Promise<IntakeResponse> {
    const client = getHttpClient()
    const response = await client.get(`${ENDPOINTS.intake}?planDate=${encodeURIComponent(planDate)}`)
    return response.data as IntakeResponse
  },

  async live(planDate: string): Promise<LiveTripsResponse> {
    const client = getHttpClient()
    const response = await client.get(`${ENDPOINTS.live}?planDate=${encodeURIComponent(planDate)}`)
    return response.data as LiveTripsResponse
  },

  async confirmArrival(referenceId: string, planDate: string): Promise<void> {
    const client = getHttpClient()
    await client.post(
      ENDPOINTS.arrival,
      { referenceId, planDate },
      { headers: { 'Idempotency-Key': idempotencyKey() } },
    )
  },

  async recordDriverMilestone(referenceId: string, planDate: string, milestone: DriverMilestone): Promise<void> {
    const client = getHttpClient()
    await client.post(
      ENDPOINTS.milestone,
      { referenceId, planDate, milestone },
      { headers: { 'Idempotency-Key': idempotencyKey() } },
    )
  },

  async gps(): Promise<FleetMapResponse> {
    const client = getHttpClient()
    const response = await client.get(ENDPOINTS.gps)
    return response.data as FleetMapResponse
  },

  /** Live trips for a date — the driver app's and the live dashboard's source. */
  async liveTrips(planDate: string): Promise<LiveTripsResponse> {
    const client = getHttpClient()
    const response = await client.get(`${ENDPOINTS.live}?planDate=${encodeURIComponent(planDate)}`)
    return response.data as LiveTripsResponse
  },

  /**
   * Record a driver milestone.
   *
   * Idempotent on the backend: recording the same milestone twice returns 200
   * rather than creating a duplicate, so a driver double-tapping in a cab or
   * retrying after a dead spot cannot corrupt the stop log.
   */
  async recordMilestone(request: MilestoneRequest): Promise<{ recorded: boolean }> {
    const client = getHttpClient()
    const response = await client.post(ENDPOINTS.milestone, request, {
      headers: { 'Idempotency-Key': idempotencyKey() },
    })
    return response.data as { recorded: boolean }
  },

  /** Active routes, for the planner's route picker. */
  async listRoutes(): Promise<DeliveryRouteDto[]> {
    const client = getHttpClient()
    const response = await client.get(
      `${ENDPOINTS.routes}?populate=checkpoints&pagination[pageSize]=100&sort=code:asc`,
    )
    const body = response.data as { data?: DeliveryRouteDto[] }
    return body.data ?? []
  },

  async getSettings(): Promise<PlanningSettings> {
    const client = getHttpClient()
    const response = await client.get(ENDPOINTS.settings)
    return (response.data as { data: PlanningSettings }).data
  },

  async updateSettings(settings: PlanningSettings): Promise<PlanningSettings> {
    const client = getHttpClient()
    const response = await client.put(
      ENDPOINTS.settings,
      { data: settings },
      { headers: { 'Idempotency-Key': idempotencyKey() } },
    )
    return (response.data as { data: PlanningSettings }).data
  },
}
